import React, { useState } from "react";
import {
  FaLightbulb,
  FaLock,
  FaUnlock,
  FaPlus,
  FaMinus,
  FaToggleOn,
  FaToggleOff,
  FaVideo,
} from "react-icons/fa";
import { useLocation } from "react-router-dom";

const Room = () => {
  const location = useLocation();
  const roomData = location.state; // ✅ Retrieve passed state
  const [brightness, setBrightness] = useState(roomData.brightness || 50);
  const [climate, setClimate] = useState(roomData.climate || 74);
  const [locked, setLocked] = useState(roomData.locked || false);
  const [lightState, setLightState] = useState(roomData.state || "ON");

  if (!roomData) {
    return <p className="text-white">No room data found.</p>; // ✅ Prevent null crash
  }

  // Collect recent activity from devices
  const recentActivities = roomData.devices
    ? roomData.devices.flatMap((device) => device.recentActivity || [])
    : [];

  return (
    <div className="p-6 text-white">
      <h1 className="text-2xl font-bold mb-6">{roomData.room}</h1>

      {/* Lighting & Climate Controls */}
      <div className="flex flex-col md:flex-row gap-6 mb-6">
        {/* Lighting Control */}
        <div className="bg-gray-800 p-6 rounded-xl flex flex-col items-center justify-center w-full md:w-1/2">
          <h3 className="text-xl font-semibold mb-4">Lighting Control</h3>
          <div className="flex items-center gap-4 mb-4">
            <FaLightbulb
              className={`text-5xl ${
                lightState === "ON" ? "text-yellow-400" : "text-gray-500"
              }`}
            />
            <button
              onClick={() => setLightState(lightState === "ON" ? "OFF" : "ON")}
              className="bg-gray-700 px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-600"
            >
              {lightState === "ON" ? <FaToggleOn /> : <FaToggleOff />}
              {lightState}
            </button>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={brightness}
            onChange={(e) => setBrightness(e.target.value)}
            className="w-full"
          />
          <p className="mt-2 text-sm">Brightness: {brightness}%</p>
        </div>

        {/* Climate Control */}
        <div className="bg-gray-800 p-6 rounded-xl flex flex-col items-center justify-center w-full md:w-1/2">
          <h3 className="text-xl font-semibold mb-4">Climate Control</h3>
          <p className="text-5xl font-bold mb-4">{climate}°</p>
          <div className="flex justify-center gap-6">
            <button
              onClick={() => setClimate(climate - 1)}
              className="bg-gray-700 p-3 rounded-full hover:bg-gray-600"
            >
              <FaMinus />
            </button>
            <button
              onClick={() => setClimate(climate + 1)}
              className="bg-gray-700 p-3 rounded-full hover:bg-gray-600"
            >
              <FaPlus />
            </button>
          </div>
        </div>
      </div>

      {/* Security & Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Security Card */}
        <div className="bg-gray-800 p-6 rounded-xl flex flex-col items-center justify-center">
          <h3 className="text-xl font-semibold mb-4">Security</h3>
          <button
            onClick={() => setLocked(!locked)}
            className={`px-6 py-3 rounded text-white font-bold flex items-center gap-2 transition ${
              locked ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {locked ? <FaLock /> : <FaUnlock />}
            {locked ? "Armed" : "Disarmed"}
          </button>
        </div>

        {/* Recent Activity Card */}
        <div className="bg-gray-800 p-6 rounded-xl">
          <h3 className="text-xl font-semibold mb-4">Recent Activity</h3>
          <ul className="space-y-2 text-sm">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <li key={index} className="text-gray-300 border-b border-gray-700 pb-1">
                  {activity.time} - {activity.message}
                </li>
              ))
            ) : (
              <li className="text-gray-400">No recent activity</li>
            )}
          </ul>
        </div>
      </div>

      {/* Camera Preview (if available) */}
      {roomData.devices &&
        roomData.devices.some((d) => d.type === "camera") && (
          <div className="bg-gray-800 p-6 rounded-xl">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <FaVideo /> Camera Preview
            </h3>
            {roomData.devices
              .filter((d) => d.type === "camera")
              .map((cam) => (
                <div key={cam.id} className="mb-4">
                  <img
                    src={cam.lastSavedPic}
                    alt="Camera Snapshot"
                    className="rounded w-full h-48 object-cover mb-2"
                  />
                  <button
                    onClick={() => window.open(cam.streamUrl, "_blank")}
                    className="bg-blue-500 px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Open Live Stream
                  </button>
                </div>
              ))}
          </div>
        )}
    </div>
  );
};

export default Room;
